// services/chatApi.ts
import type { Message, ChatHistoryEntry, ChatResponse, ResponseMessage, ConversationLog, FeedbackType } from "../store/types/chatTypes";
import type { Domain } from "@ethos/shared-types";
import { telemetry } from "./telemetryService";
import { getSessionId } from "./sessionService";

/**
 * Chat API Service
 *
 * Handles all chat and chat-history-related API calls.
 * All requests include trace context for distributed tracing.
 */

/**
 * Helper to make traced fetch requests
 * Includes session ID header for anonymous user identification
 */
async function tracedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const headers = new Headers(init?.headers);

    // Add trace headers
    const traceHeaders = telemetry.getTraceHeaders();
    Object.entries(traceHeaders).forEach(([key, value]) => {
        headers.set(key, value);
    });

    // Add guest ID header for anonymous user tracking
    headers.set("X-Guest-Id", getSessionId());

    return fetch(input, {
        ...init,
        headers,
        credentials: "include"
    });
}

export const chatApi = {
    // ============================================================
    // Chat History Endpoints
    // ============================================================

    /**
     * Fetch all chat histories for a user
     */
    async getChatHistories(userId: string): Promise<ChatHistoryEntry[]> {
        return telemetry.withSpan("chatApi.getChatHistories", async span => {
            span.setAttribute("user.id", userId);

            const response = await tracedFetch(`/api/chat-history?userId=${userId}`);

            if (!response.ok) {
                throw new Error("Failed to fetch chat histories");
            }

            const chatHistories = await response.json() as ChatHistoryEntry[];
            span.setAttribute("chatHistories.count", chatHistories.length);
            return chatHistories;
        });
    },

    /**
     * Create a new chat history
     */
    async createChatHistory(userId: string, title?: string): Promise<ChatHistoryEntry> {
        return telemetry.withSpan("chatApi.createChatHistory", async span => {
            span.setAttribute("user.id", userId);
            span.setAttribute("chatHistory.title", title || "New Chat");

            const response = await tracedFetch("/api/chat-history", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    userId,
                    title: title || "New Chat"
                })
            });

            if (!response.ok) {
                throw new Error("Failed to create chat history");
            }

            const chatHistory = await response.json() as ChatHistoryEntry;
            span.setAttribute("chatHistory.id", chatHistory.id);
            return chatHistory;
        });
    },

    /**
     * Delete a chat history
     */
    async deleteChatHistory(conversationId: string): Promise<void> {
        return telemetry.withSpan("chatApi.deleteChatHistory", async span => {
            span.setAttribute("conversation.id", conversationId);

            const response = await tracedFetch(`/api/chat-history/${conversationId}`, {
                method: "DELETE"
            });

            if (!response.ok) {
                throw new Error("Failed to delete chat history");
            }
        });
    },

    // ============================================================
    // Message Endpoints
    // ============================================================

    /**
     * Fetch messages for a chat history
     */
    async getMessages(conversationId: string): Promise<Message[]> {
        return telemetry.withSpan("chatApi.getMessages", async span => {
            span.setAttribute("conversation.id", conversationId);

            const response = await tracedFetch(`/api/chat-history/${conversationId}/messages`);

            if (!response.ok) {
                throw new Error("Failed to fetch messages");
            }

            const messages = await response.json() as Message[];
            span.setAttribute("messages.count", messages.length);
            return messages;
        });
    },

    /**
     * Send a message in a chat history
     */
    async sendMessage(conversationId: string, content: string, userId: string): Promise<{ userMessage: Message; assistantMessage: Message }> {
        return telemetry.withSpan("chatApi.sendMessage", async span => {
            span.setAttribute("conversation.id", conversationId);
            span.setAttribute("user.id", userId);
            span.setAttribute("message.length", content.length);

            const response = await tracedFetch(`/api/chat-history/${conversationId}/messages`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ content, userId })
            });

            if (!response.ok) {
                throw new Error("Failed to send message");
            }

            return await response.json() as { userMessage: Message; assistantMessage: Message };
        });
    },

    /**
     * Clear all messages in a chat history
     */
    async clearMessages(conversationId: string): Promise<void> {
        return telemetry.withSpan("chatApi.clearMessages", async span => {
            span.setAttribute("conversation.id", conversationId);

            const response = await tracedFetch(`/api/chat-history/${conversationId}/messages`, {
                method: "DELETE"
            });

            if (!response.ok) {
                throw new Error("Failed to clear chat history");
            }
        });
    },

    // ============================================================
    // Chat Endpoints (for Chat Page)
    // ============================================================

    /**
     * Send a chat message to the AI
     * Note: Returns Response directly for streaming support
     */
    async sendChatMessage(messages: ResponseMessage[], conversationId?: string, domain?: string, stream?: boolean): Promise<Response> {
        // Generate trace context for this chat request
        const traceContext = telemetry.generateTraceContext();

        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            traceparent: traceContext.traceparent,
            "X-Guest-Id": getSessionId()
        };

        // Track the chat request as an event
        void telemetry.trackEvent({
            name: "ChatMessageSent",
            properties: {
                conversationId: conversationId || "new",
                messageCount: String(messages.length)
            }
        });

        return fetch("/api/chat", {
            method: "POST",
            headers,
            credentials: "include",
            body: JSON.stringify({
                messages,
                conversation_id: conversationId,
                ...(domain !== undefined && { domain }),
                ...(stream && { stream: true })
            })
        });
    },

    /**
     * Fetch the list of active domains for the domain selector.
     * Returns empty array when no domains are configured.
     */
    async fetchDomains(): Promise<{ domains: Domain[] }> {
        const response = await fetch("/api/domains", { credentials: "include" });
        if (!response.ok) {
            throw new Error("Failed to fetch domains");
        }
        return response.json() as Promise<{ domains: Domain[] }>;
    },

    /**
     * Log a conversation (user message + assistant response)
     */
    async logConversation(conversationId: string | undefined, userMessage: string, assistantResponse: ChatResponse): Promise<ConversationLog> {
        return telemetry.withSpan("chatApi.logConversation", async span => {
            span.setAttribute("conversation.id", conversationId || "new");
            span.setAttribute("user_message.length", userMessage.length);
            span.setAttribute("assistant_response.length", assistantResponse.answer?.length || 0);

            const response = await tracedFetch("/api/chat/conversation", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    conversation_id: conversationId,
                    user_message: userMessage,
                    assistant_response: assistantResponse
                })
            });

            if (!response.ok) {
                throw new Error(response.statusText || "Failed to generate chat log");
            }

            const log = await response.json() as ConversationLog;
            span.setAttribute("logged_conversation.id", log.conversation_id);
            return log;
        });
    },

    // ============================================================
    // Feedback Endpoints
    // ============================================================

    /**
     * Submit feedback for a message (thumbs up/down)
     */
    async submitMessageFeedback(messageId: string, type: FeedbackType, comment?: string): Promise<void> {
        return telemetry.withSpan("chatApi.submitMessageFeedback", async span => {
            span.setAttribute("message.id", messageId);
            span.setAttribute("feedback.type", type ?? "none");

            const response = await tracedFetch(`/api/messages/${messageId}/feedback`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type, comment })
            });

            if (!response.ok) {
                throw new Error("Failed to submit feedback");
            }
        });
    },

    /**
     * Submit chat response feedback (rating)
     */
    async submitChatFeedback(rating: number, messageId: string): Promise<void> {
        return telemetry.withSpan("chatApi.submitChatFeedback", async span => {
            span.setAttribute("message.id", messageId);
            span.setAttribute("feedback.rating", rating);

            const response = await tracedFetch("/api/chat/response_feedback", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    feedback: rating,
                    message_id: messageId
                })
            });

            if (!response.ok) {
                throw new Error("Failed to submit feedback");
            }
        });
    },

    /**
     * Submit additional chat feedback with options and user input
     */
    async submitChatAdditionalFeedback(
        feedbackOptions: { option_id: number; option_text: string }[],
        feedbackUserInput: string,
        messageId: string
    ): Promise<void> {
        return telemetry.withSpan("chatApi.submitChatAdditionalFeedback", async span => {
            span.setAttribute("message.id", messageId);
            span.setAttribute("feedback.options_count", feedbackOptions.length);

            const response = await tracedFetch("/api/chat/response_additional_feedback", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    feedback_options: feedbackOptions.map(option => ({
                        optionId: option.option_id,
                        optionText: option.option_text
                    })),
                    feedback_user_input: feedbackUserInput,
                    message_id: messageId
                })
            });

            if (!response.ok) {
                throw new Error("Failed to submit additional feedback");
            }
        });
    }
};
